import React, { useState, useEffect } from 'react';
import FileManager from './FileManager';

// Admin Dashboard Component - 3-TIER ROLE-BASED VERSION
const AdminDashboard = ({ user, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [userRole, setUserRole] = useState(null); // 'admin' or 'editor'
  const [stats, setStats] = useState({
    users: 0,
    groups: 0,
    folders: 0,
    documents: 0,
    totalSize: 0
  });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('');
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchDashboardData();
    detectUserRole();
  }, []);

  useEffect(() => {
    setSearchTerm(''); // Clear search when switching tabs
    setSortConfig({ key: null, direction: 'asc' }); // Reset sort
    if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'groups') fetchGroups();
    else if (activeTab === 'folders') fetchFolders();
    else if (activeTab === 'documents') fetchDocuments();
  }, [activeTab]);

  const detectUserRole = async () => {
    try {
      // Try to access admin-only stats endpoint to determine role
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });

      if (response.ok) {
        // User can access admin endpoints - check if full admin or editor
        const usersResponse = await fetch(`${window.rwcvaultData.apiUrl}/admin/users`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
          }
        });

        if (usersResponse.ok) {
          const users = await usersResponse.json();
          const currentUser = users.find(u => u.username === user.username);

          if (currentUser && currentUser.groups) {
            const groups = currentUser.groups.split(', ');
            if (groups.includes('Administrators')) {
              setUserRole('admin');
            } else if (groups.includes('Editors')) {
              setUserRole('editor');
            } else {
              setUserRole('regular');
            }
          }
        }
      } else {
        setUserRole('regular');
      }
    } catch (err) {
      console.log('Error detecting role:', err);
      setUserRole('regular');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if current user can manage another user
  const canManageUser = (targetUser) => {
    if (userRole === 'admin') {
      return true; // Admins can manage everyone
    }

    if (userRole === 'editor') {
      // Editors can't manage admin users
      if (targetUser.groups && targetUser.groups.includes('Administrators')) {
        return false;
      }
      return true; // Can manage editors and regular users
    }

    return false; // Regular users can't manage anyone
  };

  // Get available tabs based on user role - 3-TIER SYSTEM
  const getAvailableTabs = () => {
    const tabs = [
      { id: 'overview', label: 'Overview', visible: true },
      { id: 'users', label: 'Users', visible: userRole === 'admin' || userRole === 'editor' },
      { id: 'groups', label: 'Groups', visible: userRole === 'admin' || userRole === 'editor' },
      { id: 'filemanager', label: 'File Manager', visible: userRole === 'admin' || userRole === 'editor' }
    ];

    // Only admins see Folders and Documents tabs (Editors use File Manager instead)
    if (userRole === 'admin') {
      tabs.push(
        { id: 'folders', label: 'Folders', visible: true },
        { id: 'documents', label: 'Documents', visible: true }
      );
    }

    return tabs.filter(tab => tab.visible);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/groups`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter out Viewers group completely
        let filteredGroups = data.filter(group => group.name !== 'Viewers');
        
        // If user is an editor, also filter out Administrators group
        if (userRole === 'editor') {
          filteredGroups = filteredGroups.filter(group => group.name !== 'Administrators');
        }
        
        setGroups(filteredGroups);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/folders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFolders(data);
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/documents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/${type}s/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });

      if (response.ok) {
        // Broadcast update to other tabs
        const channel = new BroadcastChannel('rwcvault_updates');
        if (type === 'document') {
          channel.postMessage({ type: 'documents_updated' });
        } else if (type === 'folder') {
          channel.postMessage({ type: 'folders_updated' });
        } else {
          channel.postMessage({ type: 'permissions_updated' });
        }
        channel.close();

        // Refresh current view
        if (type === 'user') await fetchUsers();
        else if (type === 'group') await fetchGroups();
        else if (type === 'folder') await fetchFolders();
        else if (type === 'document') await fetchDocuments();

        // Always refresh stats
        await fetchDashboardData();
      } else {
        alert(`Failed to delete ${type}`);
      }
    } catch (err) {
      console.error(`Error deleting ${type}:`, err);
      alert(`Error deleting ${type}`);
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedUsers = () => {
    const filteredUsers = users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const username = user.username.toLowerCase();
      const email = user.email.toLowerCase();
      const groups = (user.groups || '').toLowerCase();
      
      return username.includes(searchLower) ||
             fullName.includes(searchLower) ||
             email.includes(searchLower) ||
             groups.includes(searchLower);
    });

    if (!sortConfig.key) {
      return filteredUsers;
    }

    return [...filteredUsers].sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key === 'username') {
        aValue = a.username.toLowerCase();
        bValue = b.username.toLowerCase();
      } else if (sortConfig.key === 'name') {
        aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
        bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
      } else if (sortConfig.key === 'groups') {
        aValue = a.groups || '';
        bValue = b.groups || '';
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '20px'
    },
    header: {
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      padding: '16px 0',
      marginBottom: '20px',
      borderRadius: '8px'
    },
    headerContent: {
      width: '90%',
      margin: '0 auto',
      padding: '0 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '24px',
      fontWeight: '500',
      color: '#333',
      margin: 0
    },
    roleIndicator: {
      fontSize: '14px',
      color: '#6b7280',
      fontWeight: '400'
    },
    backButton: {
      background: '#6b7280',
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginBottom: '30px',
      width: '90%',
      margin: '0 auto 30px'
    },
    statCard: {
      background: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      textAlign: 'center'
    },
    statNumber: {
      fontSize: '36px',
      fontWeight: 'bold',
      color: '#4f46e5',
      margin: '10px 0'
    },
    statLabel: {
      color: '#666',
      fontSize: '16px'
    },
    tabContainer: {
      width: '90%',
      margin: '0 auto'
    },
    tabs: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px',
      borderBottom: '2px solid #e5e7eb'
    },
    tab: {
      padding: '10px 20px',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      cursor: 'pointer',
      fontSize: '16px',
      color: '#666',
      transition: 'all 0.3s ease'
    },
    activeTab: {
      color: '#4f46e5',
      borderBottomColor: '#4f46e5'
    },
    content: {
      background: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    actionBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    addButton: {
      background: '#4f46e5',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      borderBottom: '2px solid #e5e7eb',
      color: '#374151',
      fontWeight: '600'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #e5e7eb'
    },
    actionButtons: {
      display: 'flex',
      gap: '10px'
    },
    editButton: {
      color: '#3b82f6',
      cursor: 'pointer',
      textDecoration: 'underline',
      background: 'none',
      border: 'none',
      padding: 0
    },
    deleteButton: {
      color: '#ef4444',
      cursor: 'pointer',
      textDecoration: 'underline',
      background: 'none',
      border: 'none',
      padding: 0
    },
    badge: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500'
    },
    activeBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46'
    },
    inactiveBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b'
    },
    toast: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '8px',
      color: 'white',
      fontWeight: '500',
      zIndex: 1001,
      minWidth: '300px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    },
    toastSuccess: {
      backgroundColor: '#10b981'
    },
    toastError: {
      backgroundColor: '#ef4444'
    },
    toastInfo: {
      backgroundColor: '#3b82f6'
    },
    searchBar: {
      width: '300px',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    },
    sortableHeader: {
      cursor: 'pointer',
      userSelect: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    sortIcon: {
      fontSize: '12px',
      opacity: 0.5
    },
    activeSortIcon: {
      opacity: 1,
      color: '#4f46e5'
    }
  };

  const renderOverview = () => (
    <div>
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Users</div>
          <div style={styles.statNumber}>{stats.users}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Groups</div>
          <div style={styles.statNumber}>{stats.groups}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Folders</div>
          <div style={styles.statNumber}>{stats.folders}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Documents</div>
          <div style={styles.statNumber}>{stats.documents}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Storage</div>
          <div style={styles.statNumber}>{formatBytes(stats.totalSize)}</div>
        </div>
      </div>

      <div style={styles.content}>
        <h3 style={{ marginBottom: '20px' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <button
            style={styles.addButton}
            onClick={() => {
              setActiveTab('users');
              setFormType('user');
              setShowForm(true);
            }}
          >
            Add New User
          </button>
          <button
            style={{ ...styles.addButton, background: '#10b981' }}
            onClick={() => {
              setActiveTab('groups');
              setFormType('group');
              setShowForm(true);
            }}
          >
            Create Group
          </button>
          {userRole === 'admin' && (
            <>
              <button
                style={{ ...styles.addButton, background: '#f59e0b' }}
                onClick={() => {
                  setActiveTab('folders');
                  setFormType('folder');
                  setShowForm(true);
                }}
              >
                Create Folder
              </button>
              <button
                style={{ ...styles.addButton, background: '#6366f1' }}
                onClick={() => {
                  setActiveTab('documents');
                  setFormType('upload');
                  setShowForm(true);
                }}
              >
                Upload Documents
              </button>
            </>
          )}
          {(userRole === 'admin' || userRole === 'editor') && (
            <button
              style={{ ...styles.addButton, background: '#8b5cf6' }}
              onClick={() => setActiveTab('filemanager')}
            >
              Open File Manager
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderUsers = () => {
    // Define system groups for badges (NO MORE VIEWERS)
    const systemGroups = {
      'Administrators': { icon: '👑', color: '#dc2626' },
      'Editors': { icon: '✏️', color: '#f59e0b' }
    };

    const sortedUsers = getSortedUsers();

    const getSortIcon = (columnKey) => {
      if (sortConfig.key !== columnKey) {
        return <span style={styles.sortIcon}>⇅</span>;
      }
      return (
        <span style={{ ...styles.sortIcon, ...styles.activeSortIcon }}>
          {sortConfig.direction === 'asc' ? '▲' : '▼'}
        </span>
      );
    };

    return (
      <div style={styles.content}>
        <div style={styles.actionBar}>
          <h3 style={{ margin: 0 }}>Users Management</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search users..."
              style={styles.searchBar}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              style={styles.addButton}
              onClick={() => {
                setFormType('user');
                setShowForm(true);
                setSelectedItem(null);
              }}
            >
              Add New User
            </button>
          </div>
        </div>

        {loading ? (
          <p>Loading users...</p>
        ) : (
          <>
            {sortedUsers.length === 0 && searchTerm && (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                No users found matching "{searchTerm}"
              </p>
            )}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    <div style={styles.sortableHeader} onClick={() => handleSort('username')}>
                      Username {getSortIcon('username')}
                    </div>
                  </th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>
                    <div style={styles.sortableHeader} onClick={() => handleSort('name')}>
                      Name {getSortIcon('name')}
                    </div>
                  </th>
                  <th style={styles.th}>
                    <div style={styles.sortableHeader} onClick={() => handleSort('groups')}>
                      Groups {getSortIcon('groups')}
                    </div>
                  </th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Last Login</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map(user => {
                // Parse user groups
                const userGroups = user.groups ? user.groups.split(', ') : [];

                // Determine user type
                const isAdmin = userGroups.includes('Administrators');
                const isEditor = userGroups.includes('Editors');

                return (
                  <tr key={user.id}>
                    <td style={styles.td}><strong>{user.username}</strong></td>
                    <td style={styles.td}>{user.email}</td>
                    <td style={styles.td}>{user.first_name} {user.last_name}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {userGroups.length === 0 ? (
                          <span style={{ color: '#9ca3af', fontSize: '13px' }}>Regular User</span>
                        ) : (
                          userGroups.map((groupName, index) => {
                            const systemGroup = systemGroups[groupName];

                            if (systemGroup) {
                              // System group badge
                              return (
                                <span
                                  key={index}
                                  style={{
                                    ...styles.badge,
                                    backgroundColor: `${systemGroup.color}20`,
                                    color: systemGroup.color,
                                    border: `1px solid ${systemGroup.color}`,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  <span>{systemGroup.icon}</span>
                                  {groupName}
                                </span>
                              );
                            } else {
                              // Regular group badge
                              return (
                                <span
                                  key={index}
                                  style={{
                                    ...styles.badge,
                                    backgroundColor: '#f3f4f6',
                                    color: '#6b7280',
                                    border: '1px solid #e5e7eb'
                                  }}
                                >
                                  {groupName}
                                </span>
                              );
                            }
                          })
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        ...(user.is_active ? styles.activeBadge : styles.inactiveBadge)
                      }}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        {canManageUser(user) && (
                          <>
                            <button
                              style={styles.editButton}
                              onClick={() => {
                                setSelectedItem(user);
                                setFormType('user');
                                setShowForm(true);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              style={styles.deleteButton}
                              onClick={() => handleDelete('user', user.id)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                        {!canManageUser(user) && (
                          <span style={{ color: '#6b7280', fontSize: '12px' }}>
                            {isAdmin ? 'Admin Protected' : 'No Permission'}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}
      </div>
    );
  };

  const renderGroups = () => {
    // Define system groups (NO MORE VIEWERS)
    const systemGroups = {
      'Administrators': { icon: '👑', color: '#dc2626', roleCode: 'full_admin' },
      'Editors': { icon: '✏️', color: '#f59e0b', roleCode: 'can_upload' }
    };

    // Separate system groups from regular groups
    // Note: groups are already filtered by userRole in fetchGroups()
    const systemGroupsList = groups.filter(g => systemGroups[g.name]);
    const regularGroupsList = groups.filter(g => !systemGroups[g.name]);

    return (
      <div style={styles.content}>
        <div style={styles.actionBar}>
          <h3 style={{ margin: 0 }}>Groups Management</h3>
          <button
            style={styles.addButton}
            onClick={() => {
              setFormType('group');
              setShowForm(true);
              setSelectedItem(null);
            }}
          >
            Create New Group
          </button>
        </div>

        {loading ? (
          <p>Loading groups...</p>
        ) : (
          <>
            {/* System Groups Section */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                System Groups (Roles)
              </h4>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Users</th>
                    <th style={styles.th}>Role Code</th>
                    <th style={styles.th}>Created</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {systemGroupsList.map(group => {
                    const systemInfo = systemGroups[group.name];
                    return (
                      <tr key={group.id}>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '18px' }}>{systemInfo.icon}</span>
                            <strong>{group.name}</strong>
                            <span style={{
                              ...styles.badge,
                              backgroundColor: `${systemInfo.color}20`,
                              color: systemInfo.color,
                              border: `1px solid ${systemInfo.color}`
                            }}>
                              SYSTEM
                            </span>
                          </div>
                        </td>
                        <td style={styles.td}>{group.description}</td>
                        <td style={styles.td}>{group.user_count}</td>
                        <td style={styles.td}>
                          <code style={{
                            backgroundColor: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {systemInfo.roleCode}
                          </code>
                        </td>
                        <td style={styles.td}>{new Date(group.created_at).toLocaleDateString()}</td>
                        <td style={styles.td}>
                          <div style={styles.actionButtons}>
                            <button
                              style={styles.editButton}
                              onClick={() => {
                                setSelectedItem(group);
                                setFormType('group');
                                setShowForm(true);
                              }}
                            >
                              Edit
                            </button>
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>Protected</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Regular Groups Section */}
            <div>
              <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                Access Groups (Folder Permissions)
              </h4>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Users</th>
                    <th style={styles.th}>Folders</th>
                    <th style={styles.th}>Created</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {regularGroupsList.map(group => (
                    <tr key={group.id}>
                      <td style={styles.td}><strong>{group.name}</strong></td>
                      <td style={styles.td}>{group.description}</td>
                      <td style={styles.td}>{group.user_count}</td>
                      <td style={styles.td}>{group.folder_count}</td>
                      <td style={styles.td}>{new Date(group.created_at).toLocaleDateString()}</td>
                      <td style={styles.td}>
                        <div style={styles.actionButtons}>
                          <button
                            style={styles.editButton}
                            onClick={() => {
                              setSelectedItem(group);
                              setFormType('group');
                              setShowForm(true);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            style={styles.deleteButton}
                            onClick={() => handleDelete('group', group.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderFolders = () => (
    <div style={styles.content}>
      <div style={styles.actionBar}>
        <h3 style={{ margin: 0 }}>Folders Management</h3>
        <button
          style={styles.addButton}
          onClick={() => {
            setFormType('folder');
            setShowForm(true);
            setSelectedItem(null);
          }}
        >
          Create New Folder
        </button>
      </div>

      {loading ? (
        <p>Loading folders...</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Parent</th>
              <th style={styles.th}>Documents</th>
              <th style={styles.th}>Created</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {folders.map(folder => (
              <tr key={folder.id}>
                <td style={styles.td}><strong>{folder.name}</strong></td>
                <td style={styles.td}>{folder.parent_name || '—'}</td>
                <td style={styles.td}>{folder.document_count}</td>
                <td style={styles.td}>{new Date(folder.created_at).toLocaleDateString()}</td>
                <td style={styles.td}>
                  <div style={styles.actionButtons}>
                    <button
                      style={styles.editButton}
                      onClick={() => {
                        setSelectedItem(folder);
                        setFormType('folder');
                        setShowForm(true);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      style={styles.deleteButton}
                      onClick={() => handleDelete('folder', folder.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleDocumentDownload = async (docId, fileName) => {
    try {
      showToast('Starting download...', 'info');
      const response = await fetch(`${window.rwcvaultData.apiUrl}/documents/${docId}/download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showToast(`Successfully downloaded ${fileName}`, 'success');
      } else {
        showToast('Download failed. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Download error:', err);
      showToast('Download failed. Please try again.', 'error');
    }
  };

  const renderDocuments = () => (
    <div style={styles.content}>
      <div style={styles.actionBar}>
        <h3 style={{ margin: 0 }}>Documents Management</h3>
        <button
          style={styles.addButton}
          onClick={() => {
            setFormType('upload');
            setShowForm(true);
          }}
        >
          Upload Documents
        </button>
      </div>

      {loading ? (
        <p>Loading documents...</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Folder</th>
              <th style={styles.th}>Size</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Uploaded</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map(doc => (
              <tr key={doc.id}>
                <td style={styles.td}><strong>{doc.original_name}</strong></td>
                <td style={styles.td}>{doc.folder_name}</td>
                <td style={styles.td}>{formatBytes(doc.file_size)}</td>
                <td style={styles.td}>{doc.file_type.toUpperCase()}</td>
                <td style={styles.td}>{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                <td style={styles.td}>
                  <div style={styles.actionButtons}>
                    <button
                      onClick={() => handleDocumentDownload(doc.id, doc.original_name)}
                      style={styles.editButton}
                    >
                      Download
                    </button>
                    <button
                      style={styles.deleteButton}
                      onClick={() => handleDelete('document', doc.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  if (showForm) {
    return (
      <FormModal
        type={formType}
        item={selectedItem}
        userRole={userRole}
        onClose={() => {
          setShowForm(false);
          setSelectedItem(null);
        }}
        onSave={async () => {
          setShowForm(false);
          setSelectedItem(null);

          // Broadcast update to other tabs
          const channel = new BroadcastChannel('rwcvault_updates');
          if (formType === 'upload') {
            channel.postMessage({ type: 'documents_updated' });
          } else if (formType === 'folder') {
            channel.postMessage({ type: 'folders_updated' });
          } else if (formType === 'group' || formType === 'user') {
            channel.postMessage({ type: 'permissions_updated' });
          }
          channel.close();

          // Immediately refresh the current view to show changes
          if (formType === 'user') {
            await fetchUsers();
          } else if (formType === 'group') {
            await fetchGroups();
          } else if (formType === 'folder') {
            await fetchFolders();
          } else if (formType === 'upload') {
            await fetchDocuments();
          }

          // Always refresh dashboard stats
          await fetchDashboardData();
        }}
      />
    );
  }

  // Show File Manager if selected
  if (activeTab === 'filemanager') {
    return <FileManager user={user} onBack={() => setActiveTab('overview')} />;
  }

  return (
    <div style={styles.container}>
      {toast && (
        <div style={{
          ...styles.toast,
          ...(toast.type === 'success' ? styles.toastSuccess : 
              toast.type === 'error' ? styles.toastError : 
              styles.toastInfo)
        }}>
          {toast.message}
        </div>
      )}
      
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.title}>Admin Dashboard</h1>
            <div style={styles.roleIndicator}>
              {userRole === 'admin' ? 'Full Administrator' : userRole === 'editor' ? 'Editor' : 'Unknown Role'}
            </div>
          </div>
          <button onClick={onBack} style={styles.backButton}>
            Back to Portal
          </button>
        </div>
      </header>

      <div style={styles.tabContainer}>
        <div style={styles.tabs}>
          {getAvailableTabs().map(tab => (
            <button
              key={tab.id}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.activeTab : {})
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'groups' && renderGroups()}
        {activeTab === 'folders' && userRole === 'admin' && renderFolders()}
        {activeTab === 'documents' && userRole === 'admin' && renderDocuments()}
      </div>
    </div>
  );
};

// Form Modal Component - FIXED GROUP SELECTION ISSUE
const FormModal = ({ type, item, userRole, onClose, onSave }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    const initializeForm = async () => {
      setDataLoading(true);

      if (type === 'user') {
        await fetchAvailableGroups();
        if (item) {

          // Ensure user_groups is properly parsed as integers
          const userGroups = item.user_groups || [];
          const groupIds = Array.isArray(userGroups)
            ? userGroups.map(id => parseInt(id, 10)) // Force parse as integers
            : [];


          setFormData({
            username: item.username,
            email: item.email,
            first_name: item.first_name || '',
            last_name: item.last_name || '',
            is_active: item.is_active === 1 || item.is_active === true,
            groups: groupIds
          });
        } else {
          setFormData({
            username: '',
            email: '',
            password: '',
            first_name: '',
            last_name: '',
            is_active: true,
            groups: []
          });
        }
      } else if (type === 'group') {
        await fetchAvailableFolders();
        if (item) {

          // Ensure group_folders is properly parsed as integers
          const groupFolders = item.group_folders || [];
          const folderIds = Array.isArray(groupFolders)
            ? groupFolders.map(id => parseInt(id, 10)) // Force parse as integers
            : [];


          setFormData({
            name: item.name,
            description: item.description || '',
            folders: folderIds
          });
        } else {
          setFormData({
            name: '',
            description: '',
            folders: []
          });
        }
      } else if (type === 'folder') {
        await fetchAvailableFolders();
        if (item) {
          setFormData({
            name: item.name,
            parent_id: item.parent_id
          });
        } else {
          setFormData({
            name: '',
            parent_id: null
          });
        }
      } else if (type === 'upload') {
        await fetchAvailableFolders();
        setFormData({
          folder_id: ''
        });
      }

      setDataLoading(false);
    };

    initializeForm();
  }, [type, item]);

  const fetchAvailableGroups = async () => {
    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/groups`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter out Viewers group completely
        let filteredGroups = data.filter(group => group.name !== 'Viewers');
        
        // If user is an editor, also filter out Administrators group
        if (userRole === 'editor') {
          filteredGroups = filteredGroups.filter(group => group.name !== 'Administrators');
        }
        
        setAvailableGroups(filteredGroups);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const fetchAvailableFolders = async () => {
    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/folders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableFolders(data);
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      let endpoint = '';
      let method = 'POST';
      let body;

      if (type === 'user') {
        endpoint = item ? `/admin/users/${item.id}` : '/admin/users';
        method = item ? 'PUT' : 'POST';
        body = JSON.stringify(formData);
      } else if (type === 'group') {
        endpoint = item ? `/admin/groups/${item.id}` : '/admin/groups';
        method = item ? 'PUT' : 'POST';
        body = JSON.stringify(formData);
      } else if (type === 'folder') {
        endpoint = item ? `/admin/folders/${item.id}` : '/admin/folders';
        method = item ? 'PUT' : 'POST';
        body = JSON.stringify(formData);
      } else if (type === 'upload') {
        endpoint = '/admin/upload';
        const uploadFormData = new FormData();
        uploadFormData.append('folder_id', formData.folder_id);
        selectedFiles.forEach(file => {
          uploadFormData.append('documents[]', file);
        });
        body = uploadFormData;
      }

      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
      };

      if (type !== 'upload') {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(`${window.rwcvaultData.apiUrl}${endpoint}`, {
        method,
        headers,
        body
      });

      if (response.ok) {
        onSave();
      } else {
        const data = await response.json();
        setError(data.message || 'An error occurred');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modal: {
      background: 'white',
      borderRadius: '8px',
      width: '50%',
      minWidth: '600px',
      maxWidth: '900px',
      maxHeight: '85vh',
      overflow: 'auto',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      padding: '16px 20px',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#333'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '20px',
      cursor: 'pointer',
      color: '#6b7280',
      padding: '0',
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    body: {
      padding: '16px 20px',
      overflowY: 'auto',
      flex: 1
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontWeight: '500',
      color: '#374151',
      fontSize: '14px'
    },
    input: {
      width: '100%',
      padding: '6px 10px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '6px 10px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '14px',
      minHeight: '60px',
      resize: 'vertical',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '6px 10px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '14px'
    },
    checkbox: {
      marginRight: '6px'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '6px',
      fontSize: '14px',
      cursor: 'pointer'
    },
    footer: {
      padding: '16px 20px',
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      flexShrink: 0
    },
    button: {
      padding: '6px 16px',
      borderRadius: '4px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    primaryButton: {
      background: '#4f46e5',
      color: 'white'
    },
    secondaryButton: {
      background: '#e5e7eb',
      color: '#374151'
    },
    error: {
      color: '#ef4444',
      fontSize: '13px',
      marginTop: '8px',
      padding: '8px',
      backgroundColor: '#fef2f2',
      borderRadius: '4px',
      border: '1px solid #fecaca'
    }
  };

  const renderUserForm = () => {
    // Separate system groups from regular groups
    // Note: availableGroups is already filtered by userRole in fetchAvailableGroups()
    const systemGroups = ['Administrators', 'Editors'];
    const adminGroups = availableGroups.filter(g => systemGroups.includes(g.name));
    const regularGroups = availableGroups.filter(g => !systemGroups.includes(g.name));

    return (
      <>
        <div style={modalStyles.formGroup}>
          <label style={modalStyles.label}>Username *</label>
          <input
            type="text"
            style={modalStyles.input}
            value={formData.username || ''}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            disabled={item ? true : false}
            required
          />
        </div>
        <div style={modalStyles.formGroup}>
          <label style={modalStyles.label}>Email *</label>
          <input
            type="email"
            style={modalStyles.input}
            value={formData.email || ''}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>
        <div style={modalStyles.formGroup}>
          <label style={modalStyles.label}>Password {item ? '(leave blank to keep current)' : '*'}</label>
          <input
            type="password"
            style={modalStyles.input}
            value={formData.password || ''}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required={!item}
          />
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <div style={{ ...modalStyles.formGroup, flex: 1 }}>
            <label style={modalStyles.label}>First Name</label>
            <input
              type="text"
              style={modalStyles.input}
              value={formData.first_name || ''}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            />
          </div>
          <div style={{ ...modalStyles.formGroup, flex: 1 }}>
            <label style={modalStyles.label}>Last Name</label>
            <input
              type="text"
              style={modalStyles.input}
              value={formData.last_name || ''}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            />
          </div>
        </div>
        <div style={modalStyles.formGroup}>
          <label style={modalStyles.checkboxLabel}>
            <input
              type="checkbox"
              style={modalStyles.checkbox}
              checked={formData.is_active || false}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
        <div style={modalStyles.formGroup}>
          <label style={modalStyles.label}>Groups</label>
          <div style={{
            maxHeight: '250px',
            overflowY: 'auto',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            padding: '12px'
          }}>
            {/* System Groups */}
            {adminGroups.length > 0 && (
              <>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  textTransform: 'uppercase'
                }}>
                  System Roles
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  {adminGroups.map(group => {
                    // FIXED: More robust comparison using parseInt for both values
                    const formGroupIds = (formData.groups || []).map(id => parseInt(id, 10));
                    const groupId = parseInt(group.id, 10);
                    const isChecked = formGroupIds.includes(groupId);


                    return (
                      <label key={group.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: group.name === 'Administrators' ? '#fee2e2' : '#fef3c7',
                        padding: '8px',
                        borderRadius: '4px',
                        border: group.name === 'Administrators' ? '1px solid #dc2626' : '1px solid #f59e0b',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}>
                        <input
                          type="checkbox"
                          style={{ marginRight: '6px' }}
                          checked={isChecked}
                          onChange={(e) => {
                            const groups = (formData.groups || []).map(id => parseInt(id, 10));
                            if (e.target.checked) {
                              setFormData({ ...formData, groups: [...groups, groupId] });
                            } else {
                              setFormData({ ...formData, groups: groups.filter(id => id !== groupId) });
                            }
                          }}
                        />
                        <span>
                          {group.name === 'Administrators' ? '👑 ' : '✏️ '}
                          {group.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            {/* Regular Groups */}
            {regularGroups.length > 0 && (
              <>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  textTransform: 'uppercase'
                }}>
                  Access Groups
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '8px'
                }}>
                  {regularGroups.map(group => {
                    // FIXED: More robust comparison using parseInt for both values
                    const formGroupIds = (formData.groups || []).map(id => parseInt(id, 10));
                    const groupId = parseInt(group.id, 10);
                    const isChecked = formGroupIds.includes(groupId);


                    return (
                      <label key={group.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#f9fafb',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}>
                        <input
                          type="checkbox"
                          style={{ marginRight: '6px' }}
                          checked={isChecked}
                          onChange={(e) => {
                            const groups = (formData.groups || []).map(id => parseInt(id, 10));
                            if (e.target.checked) {
                              setFormData({ ...formData, groups: [...groups, groupId] });
                            } else {
                              setFormData({ ...formData, groups: groups.filter(id => id !== groupId) });
                            }
                          }}
                        />
                        {group.name}
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderGroupForm = () => (
    <>
      <div style={modalStyles.formGroup}>
        <label style={modalStyles.label}>Group Name *</label>
        <input
          type="text"
          style={modalStyles.input}
          value={formData.name || ''}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>
      <div style={modalStyles.formGroup}>
        <label style={modalStyles.label}>Description</label>
        <textarea
          style={modalStyles.textarea}
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows="2"
        />
      </div>
      <div style={modalStyles.formGroup}>
        <label style={modalStyles.label}>Folder Access</label>
        <div style={{
          maxHeight: '250px',
          overflowY: 'auto',
          border: '1px solid #e5e7eb',
          borderRadius: '4px',
          padding: '12px'
        }}>
          {availableFolders.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '8px 0' }}>No folders available</p>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '8px'
            }}>
              {availableFolders.map(folder => {
                // FIXED: More robust comparison using parseInt for both values
                const formFolderIds = (formData.folders || []).map(id => parseInt(id, 10));
                const folderId = parseInt(folder.id, 10);
                const isChecked = formFolderIds.includes(folderId);


                return (
                  <label key={folder.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f9fafb',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}>
                    <input
                      type="checkbox"
                      style={{ marginRight: '6px' }}
                      checked={isChecked}
                      onChange={(e) => {
                        const folders = (formData.folders || []).map(id => parseInt(id, 10));
                        if (e.target.checked) {
                          setFormData({ ...formData, folders: [...folders, folderId] });
                        } else {
                          setFormData({ ...formData, folders: folders.filter(id => id !== folderId) });
                        }
                      }}
                    />
                    📁 {folder.name}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );

  const renderFolderForm = () => (
    <>
      <div style={modalStyles.formGroup}>
        <label style={modalStyles.label}>Folder Name *</label>
        <input
          type="text"
          style={modalStyles.input}
          value={formData.name || ''}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>
      <div style={modalStyles.formGroup}>
        <label style={modalStyles.label}>Parent Folder</label>
        <select
          style={modalStyles.select}
          value={formData.parent_id || ''}
          onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
        >
          <option value="">— None —</option>
          {availableFolders
            .filter(f => !item || f.id !== item.id)
            .map(folder => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
        </select>
      </div>
    </>
  );

  const renderUploadForm = () => (
    <>
      <div style={modalStyles.formGroup}>
        <label style={modalStyles.label}>Select Folder *</label>
        <select
          style={modalStyles.select}
          value={formData.folder_id || ''}
          onChange={(e) => setFormData({ ...formData, folder_id: e.target.value })}
          required
        >
          <option value="">— Select Folder —</option>
          {availableFolders.map(folder => (
            <option key={folder.id} value={folder.id}>
              {folder.name}
            </option>
          ))}
        </select>
      </div>
      <div style={modalStyles.formGroup}>
        <label style={modalStyles.label}>Select PDFs *</label>
        <input
          type="file"
          multiple
          accept=".pdf"
          onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
          required
        />
        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
          Select one or more PDF files. Maximum file size: 150MB per file.
        </p>
      </div>
    </>
  );

  if (dataLoading) {
    return (
      <div style={modalStyles.overlay}>
        <div style={modalStyles.modal}>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>
            {type === 'user' && (item ? 'Edit User' : 'Add New User')}
            {type === 'group' && (item ? 'Edit Group' : 'Create New Group')}
            {type === 'folder' && (item ? 'Edit Folder' : 'Create New Folder')}
            {type === 'upload' && 'Upload Documents'}
          </h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={modalStyles.body}>
          {type === 'user' && renderUserForm()}
          {type === 'group' && renderGroupForm()}
          {type === 'folder' && renderFolderForm()}
          {type === 'upload' && renderUploadForm()}

          {error && <div style={modalStyles.error}>{error}</div>}
        </div>

        <div style={modalStyles.footer}>
          <button
            style={{ ...modalStyles.button, ...modalStyles.secondaryButton }}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            style={{ ...modalStyles.button, ...modalStyles.primaryButton }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : (item ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;